import {
  evaluatorIsMeanRecommender,
  getActiveEvaluationIds,
  getActiveFunders,
  getActiveProposals,
  getActiveRecommenders,
  getEvaluableRequestMax,
  getEvaluableRequestMaxStandard,
  getEvaluatorMvfProfiles,
  getFunderNetRecommenderAllocations,
  getFunderTrancheProfile,
  getProposalOrPlaceholder,
} from "@engine/round/accessors";
import type {
  Funder,
  ProposalWithFieldResponsesId,
  Recommender,
  RoundAggregate,
} from "@engine/round/aggregate";
import {
  algoInputPartsEqual,
  getRecommenderAlgoInputParts,
} from "@engine/round/aggregate";
import type { Evaluator } from "@lib/types";
import {
  type MVFProfile,
  type Tranche,
  type TrancheProfile,
  tranchesFromMVFProfile,
} from "@lib/tranche";
import { assert } from "@lib/utils/utils";
import Matrix from "algorithm/matrix";
import Vector from "algorithm/vector";
import { clamp } from "lib/utils/clamp";
import { getEvaluableIndicesMap } from "./getEvaluableIndicesMap";
import { trancheListToAlgoTranches } from "./trancheListToAlgoInterface";

const fundableProposalIdsCache: {
  cacheForParts: unknown[] | null;
  activeProposalIds: Set<string> | null;
  byRecommenderId: Map<string, Set<string>>;
} = {
  cacheForParts: null,
  activeProposalIds: null,
  byRecommenderId: new Map(),
};

export function getFundableProposalIds(
  round: RoundAggregate,
  recommender: Recommender,
  minGrantExcludedProposalIds: { [proposalId: string]: number },
): Set<string> {
  const parts = getRecommenderAlgoInputParts(round);

  if (
    fundableProposalIdsCache.cacheForParts == null ||
    !algoInputPartsEqual(fundableProposalIdsCache.cacheForParts, parts)
  ) {
    fundableProposalIdsCache.cacheForParts = parts;
    fundableProposalIdsCache.activeProposalIds = null;
    fundableProposalIdsCache.byRecommenderId = new Map();
  }

  let fundable = fundableProposalIdsCache.byRecommenderId.get(recommender.id);

  if (fundable == null) {
    fundableProposalIdsCache.activeProposalIds ??= new Set(
      getActiveProposals(round).map((proposal) => proposal.id),
    );
    const activeProposalIds = fundableProposalIdsCache.activeProposalIds;
    const { standard, matching } = getEvaluatorMvfProfiles(
      round,
      recommender.id,
    );
    // Mean recommendations and deferrals can have positive matching value
    // after their standard curve rounds to zero. Capacity and payment blocks
    // must cover every request either allocation phase can fund.
    fundable = new Set(
      [...Object.entries(standard), ...Object.entries(matching)]
        .filter(
          ([proposalId, mvf]) =>
            activeProposalIds.has(proposalId) &&
            mvf.some((point) => point.value > 0),
        )
        .map(([proposalId]) => proposalId),
    );
    fundableProposalIdsCache.byRecommenderId.set(recommender.id, fundable);
  }

  if (Object.keys(minGrantExcludedProposalIds).length === 0) {
    return fundable;
  }

  return new Set(
    [...fundable].filter(
      (proposalId) => !(proposalId in minGrantExcludedProposalIds),
    ),
  );
}

export type FunderLayerInput = {
  funderPreferenceLists: Matrix<"tranche", "recommender+hold">[];
  funderBudgets: Vector<"funder">;
  funders: Funder[];
};

export type RecommenderLayerInput = {
  recommenderPreferenceLists: Matrix<"tranche", "proposal+hold">[];
  orderedRecommenders: Recommender[];
  orderedProposals: ProposalWithFieldResponsesId[];
  recommenderIndicesMap: { [recommenderId: string]: number };
  proposalIndicesMap: { [proposalId: string]: number };
  proposalMax: { [proposalId: string]: number };
  holdProposal: ProposalWithFieldResponsesId;
};

export type RecommenderPhase =
  | { kind: "standard" }
  | {
      kind: "matching";
      initialGuarantees: Matrix<"recommender", "evaluable+hold">;
    };

export const STANDARD_RECOMMENDER_PHASE: RecommenderPhase = {
  kind: "standard",
};

let getRecommenderLayerInputCache: {
  cacheForParts: unknown[];
  cacheForExcludedKey: string;
  cachedValue: RecommenderLayerInput;
} | null = null;
const EMPTY_EXCLUDED_PROPOSAL_IDS: { [proposalId: string]: number } = {};

function excludedProposalIdsKey(excludedProposalIds: {
  [proposalId: string]: number;
}): string {
  return Object.keys(excludedProposalIds).sort().join("\0");
}

export function getRecommenderLayerInput(
  round: RoundAggregate,
  excludedProposalIds: {
    [proposalId: string]: number;
  } = EMPTY_EXCLUDED_PROPOSAL_IDS,
): RecommenderLayerInput {
  const parts = getRecommenderAlgoInputParts(round);
  const excludedKey = excludedProposalIdsKey(excludedProposalIds);

  if (
    getRecommenderLayerInputCache != null &&
    algoInputPartsEqual(getRecommenderLayerInputCache.cacheForParts, parts) &&
    getRecommenderLayerInputCache.cacheForExcludedKey === excludedKey
  ) {
    return getRecommenderLayerInputCache.cachedValue;
  }

  const value = getRecommenderLayerInputUncached(round, excludedProposalIds);
  getRecommenderLayerInputCache = {
    cacheForParts: parts,
    cacheForExcludedKey: excludedKey,
    cachedValue: value,
  };
  return value;
}

type RecommenderLayerScaffold = Omit<
  RecommenderLayerInput,
  "recommenderPreferenceLists" | "proposalMax"
> & {
  proposalMaxStandard: { [proposalId: string]: number };
  proposalMaxMatching: { [proposalId: string]: number };
};

let recommenderLayerScaffoldCache: {
  cacheForParts: unknown[];
  cacheForExcludedKey: string;
  cachedValue: RecommenderLayerScaffold;
} | null = null;

function getRecommenderLayerScaffold(
  round: RoundAggregate,
  excludedProposalIds: { [proposalId: string]: number },
): RecommenderLayerScaffold {
  const parts = getRecommenderAlgoInputParts(round);
  const excludedKey = excludedProposalIdsKey(excludedProposalIds);

  if (
    recommenderLayerScaffoldCache != null &&
    algoInputPartsEqual(recommenderLayerScaffoldCache.cacheForParts, parts) &&
    recommenderLayerScaffoldCache.cacheForExcludedKey === excludedKey
  ) {
    return recommenderLayerScaffoldCache.cachedValue;
  }

  const recommenders = getActiveRecommenders(round);
  const recommenderIndicesMap = getEvaluableIndicesMap(
    recommenders.map((recommender) => recommender.id),
    round.holdProposalId,
  );
  const orderedRecommenders: Recommender[] = [];

  for (const recommender of recommenders) {
    const recommenderIndex = recommenderIndicesMap[recommender.id];

    if (recommenderIndex != null) {
      orderedRecommenders[recommenderIndex - 1] = recommender;
    }
  }

  const proposals = getActiveProposals(round).filter(
    (proposal) => !(proposal.id in excludedProposalIds),
  );
  const proposalIndicesMap = getEvaluableIndicesMap(
    proposals.map((proposal) => proposal.id),
    round.holdProposalId,
  );
  const proposalMaxStandard: { [proposalId: string]: number } = {};
  const proposalMaxMatching: { [proposalId: string]: number } = {};

  for (const proposal of proposals) {
    const maxGrantAmount = getEvaluableRequestMax(round, proposal.id) ?? 0;
    const maxStandardAmount =
      getEvaluableRequestMaxStandard(round, proposal.id) ?? 0;

    if (maxGrantAmount > 0) {
      proposalMaxMatching[proposal.id] = maxGrantAmount;
    }

    const standardMax =
      maxStandardAmount > 0
        ? maxGrantAmount > 0
          ? Math.min(maxGrantAmount, maxStandardAmount)
          : maxStandardAmount
        : maxGrantAmount;

    if (standardMax > 0) {
      proposalMaxStandard[proposal.id] = standardMax;
    }
  }

  const proposalsById = new Map(
    proposals.map((proposal) => [proposal.id, proposal]),
  );
  const orderedProposals: ProposalWithFieldResponsesId[] = [];

  for (const [proposalId, index] of Object.entries(proposalIndicesMap)) {
    const proposal = proposalsById.get(proposalId);

    if (proposal == null) {
      throw new Error(`Missing proposal in proposalsMap: ${proposalId}`);
    }

    orderedProposals[index] = proposal;
  }

  const scaffold: RecommenderLayerScaffold = {
    proposalIndicesMap,
    proposalMaxStandard,
    proposalMaxMatching,
    orderedRecommenders,
    orderedProposals,
    recommenderIndicesMap,
    holdProposal:
      proposalsById.get(round.holdProposalId) ??
      getProposalOrPlaceholder(round, round.holdProposalId),
  };
  recommenderLayerScaffoldCache = {
    cacheForParts: parts,
    cacheForExcludedKey: excludedKey,
    cachedValue: scaffold,
  };
  return scaffold;
}

export type RecommenderLayerInputWithoutPreferenceLists = Omit<
  RecommenderLayerInput,
  "recommenderPreferenceLists"
>;

export function getMatchingRecommenderLayerInputWithoutPreferenceLists(
  round: RoundAggregate,
  excludedProposalIds: { [proposalId: string]: number },
): RecommenderLayerInputWithoutPreferenceLists {
  const scaffold = getRecommenderLayerScaffold(round, excludedProposalIds);
  return {
    proposalIndicesMap: scaffold.proposalIndicesMap,
    proposalMax: scaffold.proposalMaxMatching,
    orderedRecommenders: scaffold.orderedRecommenders,
    orderedProposals: scaffold.orderedProposals,
    recommenderIndicesMap: scaffold.recommenderIndicesMap,
    holdProposal: scaffold.holdProposal,
  };
}

export function getRecommenderLayerInputUncached(
  round: RoundAggregate,
  excludedProposalIds: { [proposalId: string]: number },
  phase: RecommenderPhase = STANDARD_RECOMMENDER_PHASE,
): RecommenderLayerInput {
  const scaffold = getRecommenderLayerScaffold(round, excludedProposalIds);
  const proposalMax =
    phase.kind === "matching"
      ? scaffold.proposalMaxMatching
      : scaffold.proposalMaxStandard;
  return {
    proposalIndicesMap: scaffold.proposalIndicesMap,
    proposalMax,
    recommenderPreferenceLists: getRecommenderPreferenceLists(
      round,
      scaffold.orderedRecommenders.map((recommender) => ({
        recommender,
        excludedProposalIds: [],
      })),
      proposalMax,
      scaffold.proposalIndicesMap,
      phase,
    ).map((matrix) =>
      matrix.rename("tranche -> tranche", "evaluable+hold -> proposal+hold"),
    ),
    orderedRecommenders: scaffold.orderedRecommenders,
    orderedProposals: scaffold.orderedProposals,
    recommenderIndicesMap: scaffold.recommenderIndicesMap,
    holdProposal: scaffold.holdProposal,
  };
}

// Real and synthetic recommenders use the same preference construction. A
// synthetic recommender may carry a proposal exclusion set.
export function getRecommenderPreferenceLists(
  round: RoundAggregate,
  recommendersWithExclusions: {
    recommender: Recommender;
    excludedProposalIds: string[];
  }[],
  evaluableMaxAllocations: { [evaluableId: string]: number | null },
  evaluableIndicesMap: { [evaluableId: string]: number },
  phase: RecommenderPhase,
): Matrix<"tranche", "evaluable+hold">[] {
  const numEvaluables = Object.keys(evaluableIndicesMap).length;

  return recommendersWithExclusions.map(
    ({ recommender, excludedProposalIds }, preferenceIndex) => {
      const whitelistedEvaluableIds = getWhitelistedEvaluableIds(
        round,
        recommender,
      );

      const trancheProfiles = getRecommenderTrancheProfiles(
        round,
        recommender,
        excludedProposalIds,
      );

      if (phase.kind === "matching") {
        const csr = getMatchingProfileCsr(
          trancheProfiles.matching,
          trancheProfiles.matchingStartScales,
          evaluableMaxAllocations,
          evaluableIndicesMap,
          whitelistedEvaluableIds,
          numEvaluables,
        );
        return buildMatchingPreferenceMatrix(
          csr,
          phase.initialGuarantees,
          preferenceIndex,
          numEvaluables,
        );
      }

      const trancheList: Tranche[] = (function () {
        return trancheProfiles.nonMatching;
      })();

      const preferenceList = trancheListToAlgoTranches(
        trancheList,
        evaluableIndicesMap,
        evaluableMaxAllocations,
        whitelistedEvaluableIds,
      );

      assert(
        preferenceList.n_cols === numEvaluables,
        "preferenceList.n_cols should be equal to numEvaluables",
      );
      return preferenceList;
    },
  );
}

export function getFunderLayerInput(
  round: RoundAggregate,
  recommenderIndicesMap: { [recommenderId: string]: number },
): FunderLayerInput {
  const funders = getActiveFunders(round);

  return {
    funders,
    funderPreferenceLists: getFunderPreferenceLists(
      round,
      funders,
      {},
      recommenderIndicesMap,
    ).map((m) =>
      m.rename("tranche -> tranche", "evaluable+hold -> recommender+hold"),
    ),
    funderBudgets: new Vector(funders.map((funder) => funder.budget)),
  };
}

type RecommenderTrancheProfiles = {
  matchingStartScales: Record<string, number>;
  matching: TrancheProfile;
  nonMatching: TrancheProfile;
};

// Building a recommender's tranche profiles from their MVFs is the dominant
// cost of the funder-decide allocator: getIncrementalEvalsData re-runs the
// two-phase layer once per recommender (to read off each one's marginal
// allocation), and these profiles depend only on (round, recommender) — never
// on the per-iteration budgets — so they're identical across every re-run.
// Memoize them per round, mirroring whitelistedEvaluableIdsCache below. The
// returned profiles are treated as read-only by their consumers
// (makeMatchingTranchesFromStart / trancheListToAlgoTranches build fresh output
// without mutating their tranche inputs), so sharing the cached objects is safe.
const recommenderTrancheProfilesCache: {
  cacheForParts: unknown[] | null;
  byCacheKey: Map<string, RecommenderTrancheProfiles>;
} = {
  cacheForParts: null,
  byCacheKey: new Map(),
};

// Drop the excluded proposals from an MVF profile before tranche conversion.
// Tranche breakpoints are computed jointly across proposals, so a per-funder
// exclusion must remove the proposal from the recommender's universe *before*
// conversion (not zero it afterward). Returns the same object when there is
// nothing effective to exclude, so a synthetic recommender with no exclusions
// keeps the exact same cached profile as today.
function omitExcludedFromMvfProfile(
  mvfProfile: MVFProfile,
  excludedProposalIds: string[],
): MVFProfile {
  if (excludedProposalIds.length === 0) {
    return mvfProfile;
  }

  const excluded = new Set(excludedProposalIds);
  let hasAny = false;

  for (const proposalId of excluded) {
    if (proposalId in mvfProfile) {
      hasAny = true;
      break;
    }
  }

  if (!hasAny) {
    return mvfProfile;
  }

  const filtered: MVFProfile = {};

  for (const [proposalId, mvf] of Object.entries(mvfProfile)) {
    if (!excluded.has(proposalId)) {
      filtered[proposalId] = mvf;
    }
  }

  return filtered;
}

function getRecommenderTrancheProfiles(
  round: RoundAggregate,
  recommender: Recommender,
  // Proposals to delete from this recommender's MVF profile before tranche
  // conversion (a per-funder exclusion routed through a synthetic recommender).
  // When empty, behavior and cache key match the pre-feature path exactly.
  excludedProposalIds: string[] = [],
): RecommenderTrancheProfiles {
  const parts = getRecommenderAlgoInputParts(round);

  if (
    recommenderTrancheProfilesCache.cacheForParts == null ||
    !algoInputPartsEqual(recommenderTrancheProfilesCache.cacheForParts, parts)
  ) {
    recommenderTrancheProfilesCache.cacheForParts = parts;
    recommenderTrancheProfilesCache.byCacheKey = new Map();
  }

  const sortedExcluded = [...excludedProposalIds].sort();
  const cacheKey = `${recommender.id}::${sortedExcluded.join(",")}`;
  const cached = recommenderTrancheProfilesCache.byCacheKey.get(cacheKey);

  if (cached) {
    return cached;
  }

  const mvfProfiles = getEvaluatorMvfProfiles(round, recommender.id);
  const standard = omitExcludedFromMvfProfile(
    mvfProfiles.standard,
    sortedExcluded,
  );
  const matching = omitExcludedFromMvfProfile(
    mvfProfiles.matching,
    sortedExcluded,
  );
  const profiles: RecommenderTrancheProfiles = {
    matchingStartScales: inferMatchingStartScales(standard, matching),
    matching: tranchesFromMVFProfile(matching),
    nonMatching: tranchesFromMVFProfile(standard),
  };
  recommenderTrancheProfilesCache.byCacheKey.set(cacheKey, profiles);
  return profiles;
}

function getLastMvfDollar(mvf: MVFProfile[string]): number | null {
  return mvf.at(-1)?.dollar ?? null;
}

// Why do we infer the scale from two MVFs? The inferred scale should always be
// exactly the drop-off rate provided by the recommender directly.
//
// We could do exactly that for real recommenders, but for the mean recommender
// we calculate mean matching MVFs from a set of matching MVFs. It's possible
// that we could use those mean MVFs to calculate a drop-off rate for each
// proposal which we store instead, or to even calculate the mean drop-off rate
// directly without the intermediate MVFs. I'm not sure if the second thing will
// work, but either way it's a fairly invasive change.
//
// The upshot is that we currently have to fall back to inferMatchingStartScales
// in at least some cases, so we might as well just use it in all cases.
function inferMatchingStartScales(
  standardMvfProfile: MVFProfile,
  matchingMvfProfile: MVFProfile,
): Record<string, number> {
  const scales: Record<string, number> = {};

  for (const [evaluableId, matchingMvf] of Object.entries(matchingMvfProfile)) {
    const standardMvf = standardMvfProfile[evaluableId];

    if (standardMvf == null) {
      continue;
    }

    const standardWidth = getLastMvfDollar(standardMvf);
    const matchingWidth = getLastMvfDollar(matchingMvf);

    if (
      standardWidth == null ||
      standardWidth <= 0 ||
      !Number.isFinite(standardWidth) ||
      matchingWidth == null ||
      !Number.isFinite(matchingWidth)
    ) {
      continue;
    }

    scales[evaluableId] = matchingWidth / standardWidth;
  }

  return scales;
}

function getFunderPreferenceLists(
  round: RoundAggregate,
  funders: Funder[],
  evaluableMaxAllocations: { [evaluableId: string]: number | null },
  evaluableIndicesMap: { [evaluableId: string]: number },
): Matrix<"tranche", "evaluable+hold">[] {
  const numEvaluables = Object.keys(evaluableIndicesMap).length;
  return funders.map((funder) => {
    const preferenceList = trancheListToAlgoTranches(
      getFunderTrancheProfile(round, funder.id),
      evaluableIndicesMap,
      evaluableMaxAllocations,
      getWhitelistedEvaluableIds(round, funder),
    );

    assert(
      preferenceList.n_cols === numEvaluables,
      "preferenceList.n_cols should be equal to numEvaluables",
    );
    return preferenceList;
  });
}

const whitelistedEvaluableIdsCache: {
  cacheForParts: unknown[] | null;
  meanRecommenderWhitelistedEvaluableIds: Set<string> | null;
  evaluatorWhitelistedEvaluableIds: Map<
    string,
    { value: Set<string>; evaluatorTag: Evaluator }
  >;
} = {
  cacheForParts: null,
  meanRecommenderWhitelistedEvaluableIds: null,
  evaluatorWhitelistedEvaluableIds: new Map(),
};

function getWhitelistedEvaluableIds(
  round: RoundAggregate,
  evaluator: Evaluator,
): Set<string> {
  const parts = getRecommenderAlgoInputParts(round);

  if (
    whitelistedEvaluableIdsCache.cacheForParts == null ||
    !algoInputPartsEqual(whitelistedEvaluableIdsCache.cacheForParts, parts)
  ) {
    whitelistedEvaluableIdsCache.cacheForParts = parts;
    whitelistedEvaluableIdsCache.meanRecommenderWhitelistedEvaluableIds = null;
    whitelistedEvaluableIdsCache.evaluatorWhitelistedEvaluableIds = new Map();
  }

  const cached =
    whitelistedEvaluableIdsCache.evaluatorWhitelistedEvaluableIds.get(
      evaluator.id,
    );

  if (cached && cached.evaluatorTag === evaluator) {
    return cached.value;
  }

  if (
    evaluator.type === "recommender" &&
    evaluatorIsMeanRecommender(evaluator)
  ) {
    if (!whitelistedEvaluableIdsCache.meanRecommenderWhitelistedEvaluableIds) {
      whitelistedEvaluableIdsCache.meanRecommenderWhitelistedEvaluableIds =
        new Set(getActiveEvaluationIds(getActiveRecommenders(round)));
    }

    const meanSet =
      whitelistedEvaluableIdsCache.meanRecommenderWhitelistedEvaluableIds;

    let hasDeferred = false;

    for (const evaluation of Object.values(evaluator.evaluations)) {
      if (evaluation.state === "deferred") {
        hasDeferred = true;
        break;
      }
    }

    if (!hasDeferred) {
      whitelistedEvaluableIdsCache.evaluatorWhitelistedEvaluableIds.set(
        evaluator.id,
        { value: meanSet, evaluatorTag: evaluator },
      );
      return meanSet;
    }

    const whitelistedEvaluableIds = new Set(meanSet);

    for (const evaluation of Object.values(evaluator.evaluations)) {
      if (evaluation.state === "deferred") {
        whitelistedEvaluableIds.add(evaluation.evaluableId);
      }
    }

    whitelistedEvaluableIdsCache.evaluatorWhitelistedEvaluableIds.set(
      evaluator.id,
      { value: whitelistedEvaluableIds, evaluatorTag: evaluator },
    );
    return whitelistedEvaluableIds;
  }

  const whitelistedEvaluableIds = new Set<string>();

  if (evaluator.type === "funder") {
    whitelistedEvaluableIds.add(round.holdProposalId);

    // Whitelist only recommenders the funder is *currently* funding (net > 0),
    // matching the collapsed tranche profile getFunderTrancheProfile emits. The
    // matrix still shows every touched recommender, but a recommender fully
    // backed out gets no budget, so it has no place on the funder's preference
    // list. (Monotonicity of the cumulative tranche table — and thus correct
    // allocation for partial backouts — is handled by the net-collapse in
    // getFunderTrancheProfile, not here.)
    for (const [evaluableId, netAmount] of Object.entries(
      getFunderNetRecommenderAllocations(round, evaluator),
    )) {
      if (netAmount > 0) {
        whitelistedEvaluableIds.add(evaluableId);
      }
    }
  } else {
    for (const evaluation of Object.values(evaluator.evaluations)) {
      if (
        evaluation.state === "evaluated" ||
        evaluation.evaluableId === round.holdProposalId ||
        evaluation.state === "deferred"
      ) {
        whitelistedEvaluableIds.add(evaluation.evaluableId);
      }
    }
  }

  whitelistedEvaluableIdsCache.evaluatorWhitelistedEvaluableIds.set(
    evaluator.id,
    { value: whitelistedEvaluableIds, evaluatorTag: evaluator },
  );
  return whitelistedEvaluableIds;
}

export type MatchingProfileCsr = {
  numTranches: number;
  trancheStart: Int32Array;
  entryCol: Int32Array;
  entryAmount: Float64Array;
  entryWritable: Uint8Array;
  colWritable: Uint8Array;
  maxByCol: Float64Array;
  scaleByCol: Float64Array;
};

// Matching profiles are exclusion-specific: two budget portions of the same
// real recommender can share a proposal index map but have different profiles.
// Key by the profile object itself so their compiled CSR data cannot collide.
const matchingProfileCsrCache = new WeakMap<
  { [evaluableId: string]: number },
  WeakMap<TrancheProfile, MatchingProfileCsr>
>();

export function getMatchingProfileCsr(
  matchingProfile: TrancheProfile,
  matchingStartScales: Record<string, number>,
  evaluableMaxAllocations: { [evaluableId: string]: number | null },
  evaluableIndicesMap: { [evaluableId: string]: number },
  whitelistedEvaluableIds: Set<string>,
  numEvaluables: number,
): MatchingProfileCsr {
  let byMatchingProfile = matchingProfileCsrCache.get(evaluableIndicesMap);

  if (byMatchingProfile == null) {
    byMatchingProfile = new WeakMap();
    matchingProfileCsrCache.set(evaluableIndicesMap, byMatchingProfile);
  }

  const cached = byMatchingProfile.get(matchingProfile);

  if (cached) {
    return cached;
  }

  const numTranches = matchingProfile.length;
  let totalEntries = 0;

  for (const tranche of matchingProfile) {
    for (const _evaluableId in tranche.allocations) {
      totalEntries++;
    }
  }

  const trancheStart = new Int32Array(numTranches + 1);
  const entryCol = new Int32Array(totalEntries);
  const entryAmount = new Float64Array(totalEntries);
  const entryWritable = new Uint8Array(totalEntries);
  let entryNo = 0;

  for (let trancheNo = 0; trancheNo < numTranches; trancheNo++) {
    trancheStart[trancheNo] = entryNo;
    const allocations = matchingProfile[trancheNo]!.allocations;

    for (const evaluableId in allocations) {
      const col = evaluableIndicesMap[evaluableId];
      entryCol[entryNo] = col ?? -1;
      entryAmount[entryNo] = allocations[evaluableId]!.amount;
      entryWritable[entryNo] =
        col != null && whitelistedEvaluableIds.has(evaluableId) ? 1 : 0;
      entryNo++;
    }
  }

  trancheStart[numTranches] = entryNo;

  const colWritable = new Uint8Array(numEvaluables);
  const maxByCol = new Float64Array(numEvaluables).fill(Infinity);
  const scaleByCol = new Float64Array(numEvaluables).fill(1);

  for (const evaluableId in evaluableIndicesMap) {
    const col = evaluableIndicesMap[evaluableId]!;

    if (whitelistedEvaluableIds.has(evaluableId)) {
      colWritable[col] = 1;
    }

    maxByCol[col] = evaluableMaxAllocations[evaluableId] ?? Infinity;
    scaleByCol[col] = matchingStartScales[evaluableId] ?? 1;
  }

  const csr: MatchingProfileCsr = {
    numTranches,
    trancheStart,
    entryCol,
    entryAmount,
    entryWritable,
    colWritable,
    maxByCol,
    scaleByCol,
  };
  byMatchingProfile.set(matchingProfile, csr);
  return csr;
}

// Reusable working buffers for buildMatchingPreferenceMatrix, grown as needed.
// It runs once per recommender per what-if re-run on the Decide tab's hot path,
// so fresh per-call allocations (especially `data`, sized tranches × proposals)
// were a measurable GC cost. Safe to share because the function is synchronous
// and non-reentrant, and its result is an exact-size copy of the scratch.
let matchingMatrixScratch: {
  advRemaining: Float64Array;
  advActive: Uint8Array;
  running: Float64Array;
  data: Float64Array;
} | null = null;

function getMatchingMatrixScratch(numEvaluables: number, dataLength: number) {
  let scratch = matchingMatrixScratch;

  if (
    scratch == null ||
    scratch.running.length < numEvaluables ||
    scratch.data.length < dataLength
  ) {
    const cols = Math.max(numEvaluables, scratch?.running.length ?? 0);
    scratch = {
      advRemaining: new Float64Array(cols),
      advActive: new Uint8Array(cols),
      running: new Float64Array(cols),
      data: new Float64Array(Math.max(dataLength, scratch?.data.length ?? 0)),
    };
    matchingMatrixScratch = scratch;
  }

  return scratch;
}

export function buildMatchingPreferenceMatrix(
  csr: MatchingProfileCsr,
  insertedInitialTranches: Matrix<"recommender", "evaluable+hold">,
  evaluatorIndex: number,
  numEvaluables: number,
): Matrix<"tranche", "evaluable+hold"> {
  const {
    numTranches,
    trancheStart,
    entryCol,
    entryAmount,
    entryWritable,
    colWritable,
    maxByCol,
    scaleByCol,
  } = csr;
  const scratch = getMatchingMatrixScratch(
    numEvaluables,
    (numTranches + 1) * numEvaluables,
  );
  // `advRemaining` needs no reset (only read where advActive is set) and
  // `data` rows are fully overwritten before use; the other two must start
  // zeroed. Views are cut to this call's exact sizes so `data.set(running, …)`
  // below writes exactly one row.
  const advRemaining = scratch.advRemaining.subarray(0, numEvaluables);
  const advActive = scratch.advActive.subarray(0, numEvaluables);
  const running = scratch.running.subarray(0, numEvaluables);
  const data = scratch.data.subarray(0, (numTranches + 1) * numEvaluables);
  advActive.fill(0);
  running.fill(0);
  const insertedData = insertedInitialTranches.data;
  const insertedRowOffset = evaluatorIndex * insertedInitialTranches.n_cols;

  for (let col = 0; col < numEvaluables; col++) {
    const amount = insertedData[insertedRowOffset + col]!;

    if (amount > 0) {
      advRemaining[col] = amount * scaleByCol[col]!;
      advActive[col] = 1;

      if (colWritable[col] === 1) {
        running[col] = Math.min(amount, maxByCol[col]!);
      }
    }
  }

  data.set(running, 0);
  let keptRows = 1;

  for (let trancheNo = 0; trancheNo < numTranches; trancheNo++) {
    const start = trancheStart[trancheNo]!;
    const end = trancheStart[trancheNo + 1]!;
    let hasAllocations = false;

    for (let entryNo = start; entryNo < end; entryNo++) {
      const col = entryCol[entryNo]!;
      const amount = entryAmount[entryNo]!;
      let newAmount = amount;

      if (col >= 0 && advActive[col] === 1) {
        const remaining = advRemaining[col]!;
        const amountToSubtract = clamp(remaining, 0, amount);
        const updatedRemaining = remaining - amountToSubtract;

        if (updatedRemaining < 1) {
          advActive[col] = 0;
        } else {
          advRemaining[col] = updatedRemaining;
        }

        newAmount = amount - amountToSubtract;

        if (newAmount < 1) {
          continue;
        }
      }

      hasAllocations = true;

      if (entryWritable[entryNo] === 1) {
        running[col] = Math.min(running[col]! + newAmount, maxByCol[col]!);
      }
    }

    if (hasAllocations) {
      data.set(running, keptRows * numEvaluables);
      keptRows++;
    }
  }

  return new Matrix<"tranche", "evaluable+hold">(
    data.slice(0, keptRows * numEvaluables),
    keptRows,
    numEvaluables,
  );
}

export function getStartingAdvancements(
  startingAllocations: { [evaluableId: string]: number },
  matchingStartScales: { [evaluableId: string]: number },
): { [evaluableId: string]: number } {
  return Object.fromEntries(
    Object.entries(startingAllocations).map(([evaluableId, amount]) => [
      evaluableId,
      amount * (matchingStartScales[evaluableId] ?? 1),
    ]),
  );
}

/**
 * Given a starting point, @param startingAllocations, startingAllocations
 * should be inserted as the first tranche for this recommender.
 * @param startingAdvancements is how far those starting allocations move along
 * each matching curve. It can be smaller than the allocation amount when the
 * matching MVF was horizontally shrunk.
 */
export function makeMatchingTranchesFromStart(
  trancheProfile: TrancheProfile,
  startingAllocations: { [evaluableId: string]: number },
  startingAdvancements: { [evaluableId: string]: number } = {},
): TrancheProfile {
  const remainingStartingAdvancements: { [evaluableId: string]: number } = {};
  const firstTrancheAllocations: Tranche["allocations"] = {};

  for (const evaluableId in startingAllocations) {
    const amount = startingAllocations[evaluableId];

    if (amount == null) {
      continue;
    }

    remainingStartingAdvancements[evaluableId] =
      startingAdvancements[evaluableId] ?? amount;
    firstTrancheAllocations[evaluableId] = {
      amount,
      bottomPlaced: false,
    };
  }

  const result: Tranche[] = [
    {
      height: 0.001,
      allocations: firstTrancheAllocations,
    },
  ];

  for (const tranche of trancheProfile) {
    const allocations: Tranche["allocations"] = {};
    let hasAllocations = false;

    for (const evaluableId in tranche.allocations) {
      const alloc = tranche.allocations[evaluableId];

      if (alloc == null) {
        continue;
      }

      if (evaluableId in remainingStartingAdvancements) {
        const remainingStartingAdvancement =
          remainingStartingAdvancements[evaluableId] ?? 0;
        const amountToSubtract = clamp(
          remainingStartingAdvancement,
          0,
          alloc.amount,
        );
        const updatedRemainingStartingAdvancement =
          remainingStartingAdvancement - amountToSubtract;

        if (updatedRemainingStartingAdvancement < 1) {
          delete remainingStartingAdvancements[evaluableId];
        } else {
          remainingStartingAdvancements[evaluableId] =
            updatedRemainingStartingAdvancement;
        }

        // Only keep the evaluable in this tranche if it has at least $1
        // allocated
        const newAmount = alloc.amount - amountToSubtract;

        if (newAmount >= 1) {
          allocations[evaluableId] = { amount: newAmount, bottomPlaced: false };
          hasAllocations = true;
        }
      } else {
        allocations[evaluableId] = alloc;
        hasAllocations = true;
      }
    }

    // Only keep a tranche if it still has allocations
    if (hasAllocations) {
      result.push({ height: tranche.height, allocations });
    }
  }

  return result;
}
